// Workflow usage and ratings (docs/12 "Usage and ratings", D73): the shapes the pages read, the
// score scale and the weights of the overall rating. The generated schema types the DTOs; the
// weights travel as a map of kind → weight and are read into a record of the known kinds here.
import type { Schemas } from "../api/client";
import { formatNumber } from "../i18n";

export type WorkflowStats = Schemas["WorkflowStatsDto"];
export type Review = Schemas["ReviewDto"];
export type ModerationReview = Schemas["ModerationReviewDto"];
export type TaskRating = Schemas["TaskRatingDto"];
export type OwnRating = Schemas["OwnRatingDto"];

/** What a rating scores, each 1–5; the overall rating weighs their averages. */
export const RATING_KINDS = ["results", "cost"] as const;
export type RatingKind = (typeof RATING_KINDS)[number];
export type Weights = Record<RatingKind, number>;

export const DEFAULT_WEIGHTS: Weights = { results: 0.6, cost: 0.4 };

export const SCORES = [1, 2, 3, 4, 5] as const;
export type Score = (typeof SCORES)[number];

export const isScore = (n: number): n is Score => SCORES.some((s) => s === n);

/** The longest review the API takes, in characters. */
export const MAX_REVIEW = 2000;

/** How many reviews a page, and so a "more", adds. */
export const REVIEW_PAGE = 10;

/** Why the caller may not rate (or edit the rating of) a task; null when they may. */
export type NotRateableReason = Schemas["NotRateableReason"];

export const EMPTY_STATS: WorkflowStats = { usage_count: 0, rating_count: 0, review_count: 0, average_results: null, average_cost: null, rating: null };

/** One decimal, in the active language: "4.4", "4,4". */
export const formatRating = (n: number) => formatNumber(n, { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** The overall rating of these averages under these weights. */
export const weigh = (averages: Weights, weights: Weights): number =>
  RATING_KINDS.reduce((sum, kind) => sum + weights[kind] * averages[kind], 0);

export interface RatingWeights {
  weights: Weights;
  isDefault: boolean;
  updatedBy: string | null;
  updatedAt: number | null;
}

/** The weights as the API sends them, every known kind present. */
export function parseRatingWeights(dto: Schemas["RatingWeightsDto"]): RatingWeights {
  const weight = (kind: RatingKind) => dto.weights[kind] ?? malformed(kind);
  return {
    weights: { results: weight("results"), cost: weight("cost") },
    isDefault: dto.is_default,
    updatedBy: dto.updated_by ?? null,
    updatedAt: dto.updated_at ?? null,
  };
}

function malformed(kind: string): never {
  throw new Error(`ocs-api sent rating weights without ${kind}`);
}
